import { Link } from "react-router-dom";
import "../commerce/commerceAdmin.css";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { apiUrl } from "../../config/api";
import "./tableeCarousel.css";

const emptySettings = {
  provider: "mercadopago_test",
  isEnabled: false,
  mode: "test",
  publicKey: "",
  accessToken: "",
  accessTokenConfigured: false,
  accessTokenPreview: "",
  webhookSecret: "",
  webhookSecretConfigured: false,
  webhookSecretPreview: "",
  bankName: "",
  bankAccountType: "",
  bankAccountNumber: "",
  bankAccountHolderName: "",
  bankAccountHolderRut: "",
  devBypassTransferEmails: false,
  frontendBaseUrl: "http://127.0.0.1:5180",
  apiPublicBaseUrl: "",
};

const providerOptions = [
  { value: "mercadopago", label: "Mercado Pago" },
  { value: "webpay", label: "Webpay" },
  { value: "bank_transfer", label: "Transferencia bancaria" },
];

const modeOptions = [
  { value: "test", label: "Prueba" },
  { value: "production", label: "Producción" },
];

const bankAccountTypeOptions = [
  { value: "Vista", label: "Vista" },
  { value: "Cuenta Corriente", label: "Cuenta Corriente" },
];

function getSessionHeaders() {
  const session = JSON.parse(localStorage.getItem("sessionData") || "{}");
  return {
    Authorization: session.sessionKey || "",
    Username: session.username || "",
    "Content-Type": "application/json",
  };
}

function getModeLabel(mode) {
  return mode === "production" ? "Producción" : "Prueba";
}

function hasPaymentSetup(settings) {
  return settings.provider === "bank_transfer" || Boolean(settings.isEnabled || settings.publicKey || settings.accessTokenConfigured);
}

function getProviderForMode(mode, providerType = "mercadopago") {
  if (providerType === "webpay") return mode === "production" ? "webpay_production" : "webpay_test";
  return mode === "production" ? "mercadopago_production" : "mercadopago_test";
}

function getOptionName(mode, providerType = "mercadopago") {
  if (providerType === "webpay") return mode === "production" ? "Webpay Producción" : "Webpay Prueba";
  return mode === "production" ? "Mercado Pago Producción" : "Mercado Pago Prueba";
}

function getProviderType(profile = {}) {
  if (String(profile.provider || "").startsWith("webpay_")) return "webpay";
  return profile.provider === "bank_transfer" ? "bank_transfer" : "mercadopago";
}

function getPaymentOptionName(profile = {}) {
  if (profile.provider === "bank_transfer") return "Transferencia bancaria";
  return getOptionName(profile.mode, getProviderType(profile));
}

function getPaymentOptionDetail(profile = {}) {
  if (profile.provider === "bank_transfer") return "Confirmación manual";
  if (getProviderType(profile) === "webpay") return "Webpay Plus";
  return "Checkout Pro";
}

function getPaymentOptionModeLabel(profile = {}) {
  if (profile.provider === "bank_transfer") return "Manual";
  return getModeLabel(profile.mode);
}

function getDefaultUrlsForMode(mode, publicBaseUrl) {
  if (mode === "production") {
    return {
      frontendBaseUrl: publicBaseUrl || "https://www.tablee.cl",
      apiPublicBaseUrl: "https://users.olivaymiel.cl",
    };
  }
  return {
    frontendBaseUrl: publicBaseUrl || "http://127.0.0.1:5180",
    apiPublicBaseUrl: "",
  };
}

function normalizeRut(value) {
  return String(value || "").replace(/[^0-9kK]/g, "").toUpperCase();
}

function formatRut(value) {
  const normalized = normalizeRut(value);
  if (!normalized) return "";
  const body = normalized.slice(0, -1);
  const checkDigit = normalized.slice(-1);
  if (!body) return checkDigit;
  const dottedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${dottedBody}-${checkDigit}`;
}

function isValidRut(value) {
  const normalized = normalizeRut(value);
  if (!/^\d{7,8}[0-9K]$/.test(normalized)) return false;
  const body = normalized.slice(0, -1);
  const checkDigit = normalized.slice(-1);
  let multiplier = 2;
  let sum = 0;

  for (let index = body.length - 1; index >= 0; index -= 1) {
    sum += Number(body[index]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }

  const remainder = 11 - (sum % 11);
  const expected = remainder === 11 ? "0" : remainder === 10 ? "K" : String(remainder);
  return expected === checkDigit;
}

function getEmptyProfileForMode(mode, publicBaseUrl) {
  const urls = getDefaultUrlsForMode(mode, publicBaseUrl);
  return {
    ...emptySettings,
    ...urls,
    mode,
    provider: getProviderForMode(mode),
  };
}

function getEmptyWebpayProfileForMode(mode, publicBaseUrl) {
  const urls = getDefaultUrlsForMode(mode, publicBaseUrl);
  return {
    ...emptySettings,
    ...urls,
    mode,
    provider: getProviderForMode(mode, "webpay"),
    publicKey: mode === "test" ? "597055555532" : "",
    accessToken: mode === "test" ? "579B532A7440BB0C9079DED94D31EA1615BACEB56610332264630D42D0A36B1C" : "",
    accessTokenConfigured: mode === "test",
    accessTokenPreview: mode === "test" ? "579B53...6B1C" : "",
  };
}

function getEmptyBankTransferProfile(publicBaseUrl) {
  const urls = getDefaultUrlsForMode("production", publicBaseUrl);
  return {
    ...emptySettings,
    ...urls,
    provider: "bank_transfer",
    mode: "production",
    isEnabled: true,
    bankAccountType: "Vista",
  };
}

function PaymentSelect({ value, options, disabled, isOpen, onOpen, onClose, onChange, placeholder = "Seleccionar" }) {
  const selectedOption = options.find((option) => String(option.value) === String(value));
  const selectedLabel = selectedOption?.label || placeholder;
  const triggerRef = React.useRef(null);
  const [menuRect, setMenuRect] = useState(null);

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return undefined;

    function updateMenuRect() {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuRect({
        left: rect.left,
        top: rect.bottom + 6,
        width: rect.width,
      });
    }

    updateMenuRect();
    window.addEventListener("resize", updateMenuRect);
    window.addEventListener("scroll", updateMenuRect, true);
    return () => {
      window.removeEventListener("resize", updateMenuRect);
      window.removeEventListener("scroll", updateMenuRect, true);
    };
  }, [isOpen]);

  const menu = isOpen && menuRect ? (
    <div
      className="tablee-payment-select__menu"
      role="listbox"
      style={{ left: menuRect.left, top: menuRect.top, width: menuRect.width }}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="option"
          aria-selected={String(option.value) === String(value)}
          className={String(option.value) === String(value) ? "is-selected" : ""}
          onClick={() => {
            onChange(option.value);
            onClose();
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  ) : null;

  return (
    <div className={`tablee-payment-select${disabled ? " is-disabled" : ""}`}>
      <button
        ref={triggerRef}
        type="button"
        className="tablee-payment-select__trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => (isOpen ? onClose() : onOpen())}
      >
        <span>{selectedLabel}</span>
        <span className="material-symbols-outlined">expand_more</span>
      </button>
      {menu ? createPortal(menu, document.body) : null}
    </div>
  );
}

function TableePaymentSettings({ apiPrefix = "/tablee", storeName = "Tablee", storeLogo = "/images/tablee_logo.png", publicBaseUrl } = {}) {
  const [settings, setSettings] = useState(emptySettings);
  const [paymentAccount, setPaymentAccount] = useState(null);
  const sharedAccount = Boolean(paymentAccount?.shared);
  const [paymentProfiles, setPaymentProfiles] = useState([]);
  const [draft, setDraft] = useState(emptySettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [openSelect, setOpenSelect] = useState("");
  const [message, setMessage] = useState("");

  const paymentOptions = useMemo(() => {
    return paymentProfiles
      .filter(hasPaymentSetup)
      .map((profile) => ({
        ...profile,
        id: profile.provider || getProviderForMode(profile.mode, getProviderType(profile)),
        name: getPaymentOptionName(profile),
        detail: getPaymentOptionDetail(profile),
        providerType: getProviderType(profile),
        status: profile.isEnabled ? "Activo" : "Inactivo",
        accessTokenPreview: profile.accessTokenConfigured ? profile.accessTokenPreview : "Sin configurar",
        webhookSecretPreview: profile.webhookSecretConfigured ? profile.webhookSecretPreview : "Sin configurar",
      }));
  }, [paymentProfiles]);

  const draftRutValue = String(draft.bankAccountHolderRut || "").trim();
  const draftRutHasValue = Boolean(draftRutValue);
  const draftRutIsValid = !draftRutHasValue || isValidRut(draftRutValue);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/payment-settings`), {
        headers: getSessionHeaders(),
      });
      const data = await response.json();
      if (!data.success) {
        setMessage(data.message || "No se pudo cargar la configuración de pagos.");
        return;
      }
      setPaymentAccount(data.paymentAccount || data.settings?.paymentAccount || null);
      const nextSettings = { ...emptySettings, ...(data.settings || {}), accessToken: "", webhookSecret: "" };
      const nextProfiles = Array.isArray(data.options)
        ? data.options.map((option) => ({ ...emptySettings, ...option, accessToken: "", webhookSecret: "" }))
        : [nextSettings];
      setSettings(nextSettings);
      setPaymentProfiles(nextProfiles);
      setDraft(nextSettings);
    } catch {
      setMessage("No se pudo conectar con la API.");
    } finally {
      setLoading(false);
    }
  }, [apiPrefix]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setModalMode("add");
    setOpenSelect("");
    setDraft({ ...settings, accessToken: "", webhookSecret: "" });
  }, [settings]);

  useEffect(() => {
    if (!showModal) return undefined;
    function handleEscape(event) {
      if (event.key === "Escape") closeModal();
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showModal, closeModal]);

  function updateDraft(field, value) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  function openModal(profile = null, mode = "edit") {
    if (sharedAccount) return;
    const missingTransfer = !paymentProfiles.some((option) => option.provider === "bank_transfer" && hasPaymentSetup(option));
    const missingMode = ["production", "test"].find((mode) => !paymentProfiles.some((option) => getProviderType(option) === "mercadopago" && option.mode === mode && hasPaymentSetup(option)));
    const missingWebpayMode = ["test", "production"].find((mode) => !paymentProfiles.some((option) => getProviderType(option) === "webpay" && option.mode === mode && hasPaymentSetup(option)));
    const baseProfile = mode === "add"
      ? (missingWebpayMode ? getEmptyWebpayProfileForMode(missingWebpayMode, publicBaseUrl) : (missingTransfer ? getEmptyBankTransferProfile(publicBaseUrl) : getEmptyProfileForMode(missingMode || "production", publicBaseUrl)))
      : { ...emptySettings, ...(profile || settings) };
    setDraft({
      ...baseProfile,
      accessToken: "",
      webhookSecret: "",
      accessTokenConfigured: mode === "edit" ? baseProfile.accessTokenConfigured : false,
      accessTokenPreview: mode === "edit" ? baseProfile.accessTokenPreview : "",
      webhookSecretConfigured: mode === "edit" ? baseProfile.webhookSecretConfigured : false,
      webhookSecretPreview: mode === "edit" ? baseProfile.webhookSecretPreview : "",
    });
    setModalMode(mode);
    setOpenSelect("");
    setMessage("");
    setShowModal(true);
  }

  async function saveSettings(event) {
    event.preventDefault();
    if (sharedAccount) return;
    if (getProviderType(draft) === "bank_transfer" && draft.isEnabled) {
      const missingBankFields = [
        draft.bankName,
        draft.bankAccountType,
        draft.bankAccountNumber,
        draft.bankAccountHolderName,
        draft.bankAccountHolderRut,
      ].some((value) => !String(value || "").trim());
      if (missingBankFields) {
        setMessage("Completa los datos bancarios para activar transferencia bancaria.");
        return;
      }
      if (!isValidRut(draft.bankAccountHolderRut)) {
        setMessage("Ingresa un RUT válido para el titular de la cuenta.");
        return;
      }
    }
    if (getProviderType(draft) === "webpay" && draft.isEnabled && draft.mode === "production") {
      if (!String(draft.publicKey || "").trim()) {
        setMessage("Ingresa el código de comercio Webpay para producción.");
        return;
      }
      if (!draft.accessTokenConfigured && !String(draft.accessToken || "").trim()) {
        setMessage("Ingresa el Api-Key-Secret de Webpay para producción.");
        return;
      }
    }
    setSaving(true);
    setMessage("");

    try {
      const response = await fetch(apiUrl(`${apiPrefix}/payment-settings`), {
        method: "PUT",
        headers: getSessionHeaders(),
        body: JSON.stringify(draft),
      });
      const data = await response.json();
      if (!data.success) {
        setMessage(data.message || "No se pudo guardar la configuración.");
        return;
      }
      const nextSettings = { ...emptySettings, ...(data.settings || {}), accessToken: "", webhookSecret: "" };
      const nextProfiles = Array.isArray(data.options)
        ? data.options.map((option) => ({ ...emptySettings, ...option, accessToken: "", webhookSecret: "" }))
        : [nextSettings];
      setSettings(nextSettings);
      setPaymentProfiles(nextProfiles);
      setDraft(nextSettings);
      setShowModal(false);
      setOpenSelect("");
      setMessage("Opción de pago actualizada.");
    } catch {
      setMessage("No se pudo guardar la configuración.");
    } finally {
      setSaving(false);
    }
  }

  async function togglePayment(profile) {
    if (sharedAccount) return;
    setMessage("");
    const nextDraft = {
      ...emptySettings,
      ...profile,
      isEnabled: !profile.isEnabled,
      accessToken: "",
      webhookSecret: "",
    };

    try {
      const response = await fetch(apiUrl(`${apiPrefix}/payment-settings`), {
        method: "PUT",
        headers: getSessionHeaders(),
        body: JSON.stringify(nextDraft),
      });
      const data = await response.json();
      if (!data.success) {
        setMessage(data.message || "No se pudo actualizar el estado.");
        return;
      }
      const nextSettings = { ...emptySettings, ...(data.settings || {}), accessToken: "", webhookSecret: "" };
      const nextProfiles = Array.isArray(data.options)
        ? data.options.map((option) => ({ ...emptySettings, ...option, accessToken: "", webhookSecret: "" }))
        : [nextSettings];
      setSettings(nextSettings);
      setPaymentProfiles(nextProfiles);
      setDraft(nextSettings);
    } catch {
      setMessage("No se pudo actualizar el estado.");
    }
  }

  async function deletePayment(profile) {
    if (sharedAccount) return;
    const provider = profile.provider || getProviderForMode(profile.mode, getProviderType(profile));
    if (!window.confirm(`¿Eliminar ${getPaymentOptionName(profile)}?`)) return;
    setMessage("");

    try {
      const response = await fetch(apiUrl(`${apiPrefix}/payment-settings/${encodeURIComponent(provider)}`), {
        method: "DELETE",
        headers: getSessionHeaders(),
      });
      const data = await response.json();
      if (!data.success) {
        setMessage(data.message || "No se pudo eliminar el medio de pago.");
        return;
      }
      const nextProfiles = Array.isArray(data.options)
        ? data.options.map((option) => ({ ...emptySettings, ...option, accessToken: "", webhookSecret: "" }))
        : [];
      const nextSettings = nextProfiles.find((option) => option.isEnabled) || nextProfiles[0] || emptySettings;
      setSettings(nextSettings);
      setPaymentProfiles(nextProfiles);
      setDraft(nextSettings);
      setMessage("Medio de pago eliminado.");
    } catch {
      setMessage("No se pudo eliminar el medio de pago.");
    }
  }

  return (
    <div className="tablee-carousel tablee-payment-settings">
      <header className="tablee-carousel__header">
        <img src={storeLogo} alt={storeName} />
        <div>
          <span>{storeName}</span>
          <h1>Opciones de pago</h1>
          <p>Administra los medios de pago disponibles para el checkout web.</p>
        </div>
        {!sharedAccount ? <button type="button" onClick={() => openModal(null, "add")}>Agregar pago</button> : null}
      </header>

      {paymentAccount ? <aside className="commerce-payment-account"><span className="material-symbols-outlined" aria-hidden="true">account_balance</span><div><strong>{sharedAccount ? "Cuenta de pagos compartida con Tablee" : `Cuenta de pagos de ${storeName}`}</strong><p>{sharedAccount ? "Los cobros llegan a la misma cuenta receptora de Tablee. Las órdenes y devoluciones de Oliva y Miel se mantienen separadas. Los medios y credenciales se administran desde Tablee." : "Esta tienda tiene su propia configuración de cuenta receptora."}</p>{sharedAccount ? <Link to="/app/tablee-pagos">Administrar cuenta compartida en Tablee</Link> : null}</div></aside> : null}
      {message ? <p className="tablee-carousel__message">{message}</p> : null}

      <section className="tablee-carousel__display tablee-payment-settings__display">
        <div className="tablee-carousel__display-head">
          <div>
            <span>Checkout web</span>
            <h2>Medios configurados</h2>
          </div>
          <strong>{paymentOptions.length} opciones</strong>
        </div>

        <div className="tablee-payment-settings__table-header" aria-hidden="true">
          <span>Medio de pago</span>
          <span>Ambiente</span>
          <span>Credenciales</span>
          <span>Estado</span>
          <span>Acciones</span>
        </div>

        <div className="tablee-payment-settings__items">
          {loading ? <p className="tablee-carousel__empty">Cargando opciones de pago...</p> : null}
          {!loading && paymentOptions.length === 0 ? (
            <p className="tablee-carousel__empty">No hay medios de pago configurados.</p>
          ) : null}
          {paymentOptions.map((option) => (
            <article key={option.id} className={!option.isEnabled ? "is-hidden" : ""}>
              <div>
                <span className="material-symbols-outlined">account_balance_wallet</span>
                <div>
                  <strong>{option.name}</strong>
                  <small>{option.detail}</small>
                </div>
              </div>
              <strong>{getPaymentOptionModeLabel(option)}</strong>
              <div>
                {option.providerType === "bank_transfer" ? (
                  <>
                    <small>Banco: {option.bankName || "Sin configurar"}</small>
                    <small>Cuenta: {[option.bankAccountType, option.bankAccountNumber].filter(Boolean).join(" · ") || "Sin configurar"}</small>
                    <small>Titular: {option.bankAccountHolderName || "Sin configurar"}</small>
                  </>
                ) : option.providerType === "webpay" ? (
                  <>
                    <small>Código comercio: {option.publicKey || "Credenciales de integración"}</small>
                    <small>Api-Key-Secret: {option.accessTokenPreview}</small>
                    <small>SDK: Webpay Plus</small>
                  </>
                ) : (
                  <>
                    <small>Public key: {option.publicKey || "Sin configurar"}</small>
                    <small>Access token: {option.accessTokenPreview}</small>
                    <small>Webhook: {option.webhookSecretPreview}</small>
                  </>
                )}
              </div>
              <div className="tablee-carousel__switch tablee-payment-settings__switch">
                <button
                  type="button"
                  className="tablee-payment-settings__switch-control"
                  role="switch"
                  aria-label={`Activar ${option.name}`}
                  aria-checked={option.isEnabled}
                  onClick={() => togglePayment(option)}
                  disabled={sharedAccount}
                >
                  <span className="tablee-payment-settings__switch-knob" />
                </button>
                <strong>{option.status}</strong>
              </div>
              <div className="tablee-payment-settings__actions">
                <button type="button" className="tablee-carousel__icon-button" onClick={() => openModal(option, "edit")} disabled={sharedAccount} aria-label={`Editar ${option.name}`}>
                  <span className="material-symbols-outlined">edit</span>
                </button>
                <button type="button" className="tablee-carousel__icon-button tablee-carousel__danger" onClick={() => deletePayment(option)} disabled={sharedAccount} aria-label={`Eliminar ${option.name}`}>
                  <span className="material-symbols-outlined">delete</span>
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {showModal ? (
        <div className="tablee-carousel__modal-backdrop" role="presentation" onMouseDown={closeModal}>
          <form
            className="tablee-carousel__modal"
            onSubmit={saveSettings}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="tablee-carousel__modal-head">
              <div>
                <span>Medio de pago</span>
                <h2>{modalMode === "edit" ? `Editar ${getPaymentOptionName(draft)}` : "Agregar pago"}</h2>
              </div>
              <button type="button" className="tablee-carousel__icon-button" onClick={closeModal} aria-label="Cerrar">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="tablee-carousel__form-grid">
              <label>
                Medio de pago
                <PaymentSelect
                  value={getProviderType(draft)}
                  options={providerOptions}
                  disabled={modalMode === "edit"}
                  isOpen={openSelect === "provider"}
                  onOpen={() => setOpenSelect("provider")}
                  onClose={() => setOpenSelect("")}
                  onChange={(value) => {
                    setDraft(value === "bank_transfer" ? getEmptyBankTransferProfile(publicBaseUrl) : value === "webpay" ? getEmptyWebpayProfileForMode("test", publicBaseUrl) : getEmptyProfileForMode("production", publicBaseUrl));
                  }}
                />
              </label>

              {getProviderType(draft) === "mercadopago" || getProviderType(draft) === "webpay" ? (
                <>
                  <label>
                    Ambiente
                    <PaymentSelect
                      value={draft.mode}
                      options={modeOptions}
                      isOpen={openSelect === "mode"}
                      onOpen={() => setOpenSelect("mode")}
                      onClose={() => setOpenSelect("")}
                      onChange={(value) => {
                        if (getProviderType(draft) === "webpay") {
                          setDraft(getEmptyWebpayProfileForMode(value, publicBaseUrl));
                          return;
                        }
                        updateDraft("mode", value);
                      }}
                    />
                  </label>

                  <label>
                    {getProviderType(draft) === "webpay" ? "Código de comercio" : "Public key"}
                    <input
                      value={draft.publicKey}
                      onChange={(event) => updateDraft("publicKey", event.target.value)}
                      placeholder={getProviderType(draft) === "webpay" ? "5970..." : "TEST-... o APP_USR-..."}
                    />
                  </label>

                  <label>
                    {getProviderType(draft) === "webpay" ? "Api-Key-Secret" : "Access token"}
                    <input
                      type="password"
                      value={draft.accessToken}
                      onChange={(event) => updateDraft("accessToken", event.target.value)}
                      placeholder={draft.accessTokenConfigured ? `Mantener ${draft.accessTokenPreview}` : getProviderType(draft) === "webpay" ? "Pega el Api-Key-Secret" : "Pega el access token"}
                    />
                  </label>

                  {getProviderType(draft) === "mercadopago" ? (
                    <label>
                      Webhook secret
                      <input
                        type="password"
                        value={draft.webhookSecret}
                        onChange={(event) => updateDraft("webhookSecret", event.target.value)}
                        placeholder={draft.webhookSecretConfigured ? `Mantener ${draft.webhookSecretPreview}` : "Opcional"}
                      />
                    </label>
                  ) : null}
                </>
              ) : null}

              {getProviderType(draft) === "bank_transfer" ? (
                <>
                  <label>
                    Banco
                    <input
                      value={draft.bankName}
                      onChange={(event) => updateDraft("bankName", event.target.value)}
                      placeholder="Nombre del banco"
                      required={draft.isEnabled}
                    />
                  </label>

                  <label>
                    Tipo de cuenta
                    <PaymentSelect
                      value={draft.bankAccountType}
                      placeholder="Selecciona tipo de cuenta"
                      options={bankAccountTypeOptions}
                      isOpen={openSelect === "bankAccountType"}
                      onOpen={() => setOpenSelect("bankAccountType")}
                      onClose={() => setOpenSelect("")}
                      onChange={(value) => updateDraft("bankAccountType", value)}
                    />
                  </label>

                  <label>
                    Cuenta
                    <input
                      value={draft.bankAccountNumber}
                      onChange={(event) => updateDraft("bankAccountNumber", event.target.value)}
                      placeholder="Número de cuenta"
                      required={draft.isEnabled}
                    />
                  </label>

                  <label>
                    Nombre
                    <input
                      value={draft.bankAccountHolderName}
                      onChange={(event) => updateDraft("bankAccountHolderName", event.target.value)}
                      placeholder="Nombre del titular"
                      required={draft.isEnabled}
                    />
                  </label>

                  <label className={`tablee-payment-settings__rut-field${draftRutHasValue && !draftRutIsValid ? " is-invalid" : ""}${draftRutHasValue && draftRutIsValid ? " is-valid" : ""}`}>
                    RUT
                    <input
                      value={draft.bankAccountHolderRut}
                      onChange={(event) => updateDraft("bankAccountHolderRut", formatRut(event.target.value))}
                      placeholder="12.345.678-9"
                      required={draft.isEnabled}
                      aria-invalid={draftRutHasValue && !draftRutIsValid}
                    />
                    {draftRutHasValue ? (
                      <small>{draftRutIsValid ? "RUT válido" : "Revisa el RUT del titular"}</small>
                    ) : null}
                  </label>

                </>
              ) : null}

              <label>
                URL pública de {storeName}
                <input
                  value={draft.frontendBaseUrl}
                  onChange={(event) => updateDraft("frontendBaseUrl", event.target.value)}
                  placeholder={publicBaseUrl || "https://tablee.cl"}
                />
              </label>

              <label className="tablee-carousel__wide-field">
                URL pública de la API
                <input
                  value={draft.apiPublicBaseUrl}
                  onChange={(event) => updateDraft("apiPublicBaseUrl", event.target.value)}
                  placeholder="https://api.tudominio.cl"
                />
              </label>

              {getProviderType(draft) === "bank_transfer" ? (
                <div className="tablee-payment-settings__dev-mode tablee-carousel__wide-field">
                  <div>
                    <strong>Modo prueba local</strong>
                    <small>Crear pedidos sin enviar correos</small>
                  </div>
                  <button
                    type="button"
                    className="tablee-payment-settings__switch-control"
                    role="switch"
                    aria-label="Modo prueba local"
                    aria-checked={draft.devBypassTransferEmails}
                    onClick={() => updateDraft("devBypassTransferEmails", !draft.devBypassTransferEmails)}
                  >
                    <span className="tablee-payment-settings__switch-knob" />
                  </button>
                </div>
              ) : null}
            </div>

            <div className="tablee-carousel__modal-actions">
              <div className="tablee-payment-settings__footer-left">
                <div className="tablee-carousel__switch tablee-payment-settings__toggle tablee-payment-settings__switch">
                  <button
                    type="button"
                    className="tablee-payment-settings__switch-control"
                    role="switch"
                    aria-label={`Activar ${getPaymentOptionName(draft)}`}
                    aria-checked={draft.isEnabled}
                    onClick={() => updateDraft("isEnabled", !draft.isEnabled)}
                  >
                    <span className="tablee-payment-settings__switch-knob" />
                  </button>
                  <strong>{draft.isEnabled ? `${getPaymentOptionName(draft)} activo` : `${getPaymentOptionName(draft)} inactivo`}</strong>
                </div>
                <p>{getProviderType(draft) !== "bank_transfer" && draft.accessTokenConfigured ? `${getProviderType(draft) === "webpay" ? "Api-Key-Secret" : "Access token"} actual: ${draft.accessTokenPreview}` : ""}</p>
              </div>
              <div className="tablee-carousel__footer-buttons tablee-payment-settings__footer-buttons">
                <button type="button" className="tablee-carousel__secondary" onClick={closeModal} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" disabled={saving || loading}>
                  {saving ? "Guardando..." : "Guardar pago"}
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

export default TableePaymentSettings;
